import { User } from "oidc-client-ts";

import type { AuthenticationResult } from "./cognitoIdp";
import { decodeJwtPayload } from "./jwt";
import { getUserManager } from "./userManager";

/**
 * Une session Touch ID n'a pas de refresh token. Sans lui, à
 * `expires_in − 60 s`, oidc-client-ts tenterait un renouvellement silencieux par
 * iframe contre le Hosted UI — qui n'a pas de session pour cet utilisateur — et
 * react-oidc-context transformerait l'échec en état ERROR : l'appli afficherait
 * une boîte d'erreur plein écran.
 *
 * On devance donc l'expiration : 90 s avant, la session est retirée et
 * l'utilisateur retombe proprement sur l'écran de connexion. `removeUser()`
 * annule aussi les minuteries d'oidc-client-ts, donc le renouvellement n'a
 * jamais lieu.
 */
const EXPIRY_MARGIN_SECONDS = 90;

let expiryTimer: number | undefined;
let unloadHooked = false;

/**
 * Pose la session Touch ID dans le store OIDC en mémoire. Toute l'appli lit
 * `auth.user?.id_token` : elle ne voit aucune différence avec une session
 * ouverte par le Hosted UI.
 *
 * Le `RefreshToken` renvoyé par l'API est volontairement ignoré (spec § 5).
 */
export async function beginPasskeySession(result: AuthenticationResult): Promise<void> {
  const manager = getUserManager();
  const accessClaims = decodeJwtPayload(result.AccessToken);
  const user = new User({
    id_token: result.IdToken,
    access_token: result.AccessToken,
    token_type: result.TokenType,
    scope: typeof accessClaims.scope === "string" ? accessClaims.scope : undefined,
    profile: decodeJwtPayload(result.IdToken) as unknown as User["profile"],
    expires_at: Math.floor(Date.now() / 1000) + result.ExpiresIn,
  });

  if (!unloadHooked) {
    manager.events.addUserUnloaded(() => window.clearTimeout(expiryTimer));
    unloadHooked = true;
  }

  await manager.storeUser(user);
  // Lève l'évènement « user loaded » : c'est lui qui fait passer
  // react-oidc-context à `isAuthenticated`.
  await manager.events.load(user);

  window.clearTimeout(expiryTimer);
  expiryTimer = window.setTimeout(
    () => void manager.removeUser(),
    Math.max(0, result.ExpiresIn - EXPIRY_MARGIN_SECONDS) * 1000,
  );
}
